import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {tap, map, filter} from 'rxjs/operators';
import {BehaviorSubject, Observable, of, Subject} from 'rxjs';
import {IAction, IWimdDate, IWimdFilters, IWimdState} from './wimdInterfaces';
import {UserSettingsService} from '../../../shared/services/user-settings/user-settings.service';
import {AuthenticationService} from '../../../shared/services/authentication/authentication.service';
import {ToastrService} from 'ngx-toastr';


@Injectable({
  providedIn: 'root'
})
export class WimdStore {

  isLoading: boolean = true;

  // Needed in components
  public idProject: number;
  public currentState: BehaviorSubject<any> = new BehaviorSubject(undefined);
  private currentDate: IWimdDate;
  private state: IWimdState;

  constructor(
    private authenticationService: AuthenticationService,
    private http: HttpClient,
    private userSettingsService: UserSettingsService,
    private toastr: ToastrService) {

  }

  initAndFetchPassages(idProject: any): void {
    this.idProject = parseInt(idProject);
    this.dispatchUpdatedState({action: 'INIT_AND_FETCH_PASSAGES'});
  }

  setFilters(filters: IWimdFilters): void {
    this.dispatchUpdatedState({action: 'SET_FILTER', payload: filters});
  }

  fetchPassages(date: IWimdDate): void {
    this.dispatchUpdatedState({action: 'FETCH_PASSAGES', payload: date});
  }

  clearCurrentState(): void {
    this.currentState.next(undefined);
  }

  dispatchUpdatedState(action: IAction): void {
    this.getUpdatedState(action).subscribe((state) => {
      this.currentState.next(state);
      this.isLoading = false;
    }, (err) => {
      this.isLoading = false;
    });
  }

  private getUpdatedState(action: IAction): Observable<any> {
    this.isLoading = true;
    let iWimdStateObservable: Observable<IWimdState>;
    switch (action.action) {
      case 'SET_FILTER':
        iWimdStateObservable = of(this.buildUpdatedStateInstance({filters: action.payload}));
        break;
      case 'FETCH_PASSAGES':
        iWimdStateObservable = this.getPassagesData(action.payload)
          .pipe(
            map((passages) => this.buildUpdatedStateInstance(passages, this.currentDate)));
        break;
      case 'INIT_AND_FETCH_PASSAGES':
        iWimdStateObservable = this.getPassagesData(action.payload = undefined)
          .pipe(
            map((passages) => this.buildNewStateInstance(passages)),
            tap((state) => this.state = state));
        break;
      default:
        iWimdStateObservable = of(Object.assign({}, this.state));
    }
    return iWimdStateObservable;
  }

  private getPassagesData(date?: any): Observable<any> {
    let dateInterval: IWimdDate = (date && date.start && date.end) ? date : this.getInitialDates();
    this.currentDate = dateInterval;

    let request = `projects/${this.idProject}/passages?date_start=${dateInterval.start}&date_end=${dateInterval.end}&reformat=1&filters=1`;

    if (date && date.confidence !== null && date.confidence !== undefined) {
      request += `&confidence=` + date.confidence.toString();
    }

    if (date && date.direction !== null && date.direction !== undefined) {
      request += `&direction=` + date.direction.toString();
    }

    if (date && date.category !== null && date.category !== undefined) {
      request += `&category=` + date.category.toString();
    }

    return this.http.get(request)
      .pipe(
        map((data: any) => {
          if (data.passages.length == 0) {
            this.toastr.error('No data on the selected dates.');
          }
          return data;
        })
      );
  }

  private buildNewStateInstance(asyncData?): any {
    asyncData = asyncData || {};
    const a = {
      date: this.getInitialDates(),
      filters: {
        thresholds: asyncData.filters ? asyncData.filters.thresholds : []
      },
      passages: asyncData.passages || [],
      chart: asyncData.chart || {
        d1: [],
        d2: []
      },
    };
    return a;
  }

  private buildUpdatedStateInstance(stateUpdates, date?: IWimdDate) {

    if (!stateUpdates) {
      stateUpdates = this.buildNewStateInstance();
    }
    if (stateUpdates.filters) {
      if (stateUpdates.filters.thresholds) {
        stateUpdates.filters.thresholds = stateUpdates.filters.thresholds.filter(
          (threshold) => threshold !== ''
        );
      }
      stateUpdates.filters = Object.assign({}, this.currentState.value.filters, stateUpdates.filters);
    }

    if (!this.authenticationService.userHasRoleOsmos() && this.currentState.value.filters.thresholds.length > 0) {
      stateUpdates.filters.thresholds = this.currentState.value.filters.thresholds;
    }
    return Object.assign({}, this.currentState.value, stateUpdates, {date});
  }

  private getInitialDates(): any {
    return {
      start: this.getUtcCurrentStartDate().toISOString(),
      end: this.getUtcCurrentDate().toISOString()
    };
  }

  getUtcCurrentStartDate() {
    return this.getUtcCurrentDate(4);
    // return this.getUtcCurrentDate(80000);
  }

  getUtcCurrentDate(modifierHour?: number): Date {
    let dateToHandle = new Date();
    const year = dateToHandle.getUTCFullYear();
    const month = dateToHandle.getUTCMonth(); // 0-based
    const day = dateToHandle.getUTCDate();
    const hour = modifierHour ? dateToHandle.getUTCHours() - modifierHour : dateToHandle.getUTCHours();
    const minute = dateToHandle.getUTCMinutes();
    return new Date(Date.UTC(year, month, day, hour, minute));
  }

}
